import React, { useEffect, useMemo, useState } from 'react';
import { getActivityLog, listOrganizations, apiFetch } from '../api';
import SearchInput from '../components/SearchInput';
import LoadingState from '../components/LoadingState';
import { useSort, sortBy, SortHead } from '../components/PortalUI';
import '../components/PortalUI.css';

// The activity log for NordComply staff (Provider Portal): who signed in, and which users, tenants and organisations were
// created, changed or removed, newest first. The backend (handleActivityLog) only answers NordComply staff.

const GROUPS = [
  ['', 'Alle'],
  ['logins', 'Logins'],
  ['users', 'Brugere'],
  ['tenants', 'Tenants'],
  ['organizations', 'Organisationer'],
];

// type -> [label, tone]
const TYPES = {
  login: ['Login', 'login'],
  'login-failed': ['Mislykket login', 'fail'],
  'account-setup': ['Konto sat op', 'login'],
  'user-created': ['Bruger oprettet', 'new'],
  'user-updated': ['Bruger ændret', 'chg'],
  'user-deleted': ['Bruger fjernet', 'del'],
  'user-approved': ['Bruger aktiveret', 'chg'],
  'user-locked': ['Konto låst', 'del'],
  'user-unlocked': ['Konto låst op', 'chg'],
  'credentials-reset': ['Adgang nulstillet', 'chg'],
  'welcome-resent': ['Velkomstmail sendt igen', 'chg'],
  'tenant-created': ['Tenant tilføjet', 'new'],
  'tenant-reconnected': ['Tenant forbundet igen', 'chg'],
  'tenant-updated': ['Tenant ændret', 'chg'],
  'tenant-deleted': ['Tenant fjernet', 'del'],
  'tenant-restored': ['Tenant gendannet', 'new'],
  'tenant-purged': ['Tenant slettet endeligt', 'del'],
  'organization-created': ['Organisation oprettet', 'new'],
  'organization-updated': ['Organisation ændret', 'chg'],
  'organization-deleted': ['Organisation fjernet', 'del'],
};

// the old tones, as the colours of the shared chips
const TONE = { login: 'ok', fail: 'warn', new: 'new', chg: 'chg', del: 'bad' };
const ROLE_LABEL = { provider: 'NordComply', admin: 'MSP-administrator', customer: 'Kunde' };

// What happened, in one plain sentence.
function describe(e) {
  const name = e.targetName || e.target || '';
  switch (e.type) {
    case 'login': return e.detail ? `Loggede ind. ${e.detail}` : 'Loggede ind';
    case 'login-failed': return e.detail || 'Mislykket login';
    case 'account-setup': return 'Satte sin konto op';
    case 'user-created': return `Oprettede brugeren ${e.target}`;
    case 'user-updated': return `Ændrede brugeren ${e.target}`;
    case 'user-deleted': return `Fjernede brugeren ${e.target}`;
    case 'user-approved': return `Aktiverede ${e.target}`;
    case 'user-locked': return `Låste kontoen ${e.target}`;
    case 'user-unlocked': return `Låste kontoen ${e.target} op`;
    case 'credentials-reset': return `Nulstillede adgang for ${e.target}`;
    case 'welcome-resent': return `Sendte velkomstmailen igen til ${e.target}`;
    case 'tenant-created': return `Tilføjede tenanten ${name}`;
    case 'tenant-reconnected': return `Forbandt tenanten ${name} igen`;
    case 'tenant-updated': return `Ændrede tenanten ${name}`;
    case 'tenant-deleted': return `Fjernede tenanten ${name}${e.detail ? `. ${e.detail}` : ''}`;
    case 'tenant-restored': return `Gendannede tenanten ${name}`;
    case 'tenant-purged': return `Slettede tenanten ${name} endeligt`;
    case 'organization-created': return `Oprettede organisationen ${name}`;
    case 'organization-updated': return `Ændrede organisationen ${name}`;
    case 'organization-deleted': return `Fjernede organisationen ${name}`;
    default: return e.type;
  }
}

// Every day shows its date; today also says "I dag".
function dayLabel(iso) {
  const d = new Date(iso);
  const date = d.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const sameDay = (x, y) => x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
  return sameDay(d, new Date()) ? `I dag · ${date}` : date;
}
// The day boundaries of the chosen dates, as the instants the server filters on.
const dayStartIso = (ymd) => (ymd ? new Date(`${ymd}T00:00:00`).toISOString() : '');
const dayEndIso = (ymd) => (ymd ? new Date(`${ymd}T23:59:59.999`).toISOString() : '');

const timeLabel = (iso) => new Date(iso).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });

function initials(text) {
  return String(text || '?').split(/[ .@]/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

export default function ActivityLog() {
  const [group, setGroup] = useState('');
  const [events, setEvents] = useState(null);
  const [next, setNext] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  // Filter by organisation and then by one of its customers.
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState('');
  const [orgTenants, setOrgTenants] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [fromDay, setFromDay] = useState('');
  const [toDay, setToDay] = useState('');

  useEffect(() => {
    listOrganizations().then((d) => setOrgs(Array.isArray(d.organizations) ? d.organizations : [])).catch(() => {});
  }, []);
  useEffect(() => {
    setTenantId('');
    setOrgTenants([]);
    if (!orgId) return;
    apiFetch(`/api/tenants?orgId=${orgId}`).then((r) => r.json()).then((d) => setOrgTenants(Array.isArray(d) ? d : [])).catch(() => {});
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    setEvents(null);
    setNext(null);
    setError('');
    getActivityLog({ group, orgId, tenantId, from: dayStartIso(fromDay), to: dayEndIso(toDay) })
      .then((d) => {
        if (cancelled) return;
        setEvents(d.events);
        setNext(d.next);
      })
      .catch((err) => !cancelled && setError(err.message));
    return () => { cancelled = true; };
  }, [group, orgId, tenantId, fromDay, toDay]);

  async function loadMore() {
    setLoadingMore(true);
    try {
      const d = await getActivityLog({ group, after: next, orgId, tenantId, from: dayStartIso(fromDay), to: dayEndIso(toDay) });
      setEvents((prev) => [...(prev || []), ...d.events]);
      setNext(d.next);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return events || [];
    return (events || []).filter((e) => [describe(e), e.actor, e.target, e.targetName, e.detail, e.ip, (TYPES[e.type] || [])[0]].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [events, search]);

  const [sort, toggleSort] = useSort('time', 'desc');
  const byTime = sort.key === 'time' && sort.dir === 'desc';
  // Newest first groups the rows under their day; any other order is one flat list.
  const rows = byTime ? shown : sortBy(shown, sort, {
    time: (e) => new Date(e.at).getTime(),
    user: (e) => e.actor || 'System',
    event: (e) => (TYPES[e.type] || [e.type])[0],
    detail: (e) => describe(e),
    ip: (e) => e.ip,
  });
  let lastDay = '';

  return (
    <div className="pu-page">
      <div className="pu-head">
        <div>
          <h1>Aktivitetslog</h1>
          <p>Logins samt oprettede, ændrede og fjernede brugere, tenants og organisationer. Gemmes i 13 måneder.</p>
        </div>
      </div>

      <div className="pu-bar">
        <div className="pu-seg" role="group" aria-label="Vis">
          {GROUPS.map(([key, label]) => (
            <button key={key || 'all'} type="button" className={group === key ? 'is-on' : ''} onClick={() => setGroup(key)}>{label}</button>
          ))}
        </div>
        <select className="pu-sel" value={orgId} onChange={(e) => setOrgId(e.target.value)} aria-label="Organisation">
          <option value="">Alle organisationer</option>
          {orgs.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
        </select>
        <select className="pu-sel" value={tenantId} onChange={(e) => setTenantId(e.target.value)} disabled={!orgId} aria-label="Kunde">
          <option value="">{orgId ? 'Alle kunder' : 'Vælg en organisation først'}</option>
          {orgTenants.map((tn) => (<option key={tn.id} value={tn.tenantId || tn.id}>{tn.name}</option>))}
        </select>
        <label className="pu-sel" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Fra<input type="date" value={fromDay} max={toDay || undefined} onChange={(e) => setFromDay(e.target.value)} aria-label="Fra dato" style={{ border: 0, background: 'transparent', font: 'inherit', color: 'inherit' }} /></label>
        <label className="pu-sel" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Til<input type="date" value={toDay} min={fromDay || undefined} onChange={(e) => setToDay(e.target.value)} aria-label="Til dato" style={{ border: 0, background: 'transparent', font: 'inherit', color: 'inherit' }} /></label>
        {(fromDay || toDay) && <button type="button" className="pu-btn" onClick={() => { setFromDay(''); setToDay(''); }}>Ryd datoer</button>}
        <SearchInput value={search} onChange={setSearch} placeholder="Søg i loggen..." />
      </div>

      {error && <p className="state-error">{error}</p>}
      {!events && !error && <LoadingState />}

      {events && (
        <div className="pu-table">
          <div className="pu-th pu-tr pu-tr--a"><SortHead label="Bruger" k="user" sort={sort} onSort={toggleSort} /><SortHead label="Hændelse" k="event" sort={sort} onSort={toggleSort} /><SortHead label="Detalje" k="detail" sort={sort} onSort={toggleSort} /><SortHead label="Tidspunkt" k="time" sort={sort} onSort={toggleSort} /><SortHead label="IP" k="ip" sort={sort} onSort={toggleSort} /></div>
          {shown.length === 0 && <p className="pu-empty">{events.length === 0 ? 'Ingen hændelser endnu. Loggen starter, den dag den tages i brug.' : 'Ingen hændelser matcher din søgning.'}</p>}
          {rows.map((e) => {
            const [label, tone] = TYPES[e.type] || [e.type, 'chg'];
            const day = dayLabel(e.at);
            const header = byTime && day !== lastDay ? <div className="pu-day" key={`d-${e.id}`}>{day}</div> : null;
            lastDay = day;
            return (
              <React.Fragment key={e.id}>
                {header}
                <div className="pu-tr pu-tr--a">
                  <span className="pu-who">
                    <span className="pu-av">{initials(e.actor)}</span>
                    <span>
                      <b title={e.actor || 'System'}>{e.actor || 'System'}</b>
                      <small>{e.actorRole ? ROLE_LABEL[e.actorRole] || e.actorRole : 'System'}</small>
                    </span>
                  </span>
                  <span><span className={`pu-chip pu-chip--${TONE[tone] || 'chg'}`}>{label}</span></span>
                  <span className="pu-c" title={describe(e)}>{describe(e)}</span>
                  <span className="pu-c pu-c--m">{timeLabel(e.at)}</span>
                  <span className="pu-c pu-c--mono">{e.ip || ''}</span>
                </div>
              </React.Fragment>
            );
          })}
          {next && !search.trim() && (
            <button type="button" className="pu-more" onClick={loadMore} disabled={loadingMore}>{loadingMore ? 'Henter…' : 'Vis flere'}</button>
          )}
        </div>
      )}
    </div>
  );
}
